import React from 'react';
import PropTypes from 'prop-types';

import { isLessThanBreakpoint } from 'dpl/util/grid';
import Icon from 'dpl/common/components/Icon';
import SmartLink from 'dpl/components/SmartLink';
import SmartImage from 'dpl/components/SmartImage';
import TooltipWrapper from 'dpl/common/tooltip/components/TooltipWrapper';
import HorizontalSlider from 'dpl/components/HorizontalSlider';

const IS_LT_LG = isLessThanBreakpoint('lg');
const IS_LT_XL = isLessThanBreakpoint('xl');
const ICON_WIDTH = IS_LT_LG ? '24px' : '30px';

let IMAGE_WIDTH;
if (IS_LT_LG) {
  IMAGE_WIDTH = '247px';
} else if (IS_LT_XL) {
  IMAGE_WIDTH = '276px';
} else {
  IMAGE_WIDTH = '363px';
}

export default function BreederProfileRelatedBreedsList({
  currentBreedId,
  relatedBreeds
}) {
  if (!relatedBreeds.length) {
    return null;
  }

  return (
    <div className="BreederProfileRelatedBreedsList">
      <h2 className="mb4 mb6-lg f4 f5-lg">Similar breeds</h2>
      <HorizontalSlider
        iconClassName="b bg-white ph3 pv3 br-100 box-shadow-strong"
        arrowClassName="pb1"
        persistentArrows
        containerClassName="right-bleeding-row row flex-nowrap"
      >
        {relatedBreeds.map(breed => {
          const {
            availability_count_text: availabilityCountText,
            breed_overview_page_url: breedOverviewPageUrl,
            id,
            name,
            non_standard: isNonStandard,
            non_standard_color_text: nonStandardColorText,
            non_standard_secondary_photo_url: nonStandardSecondaryPhotoUrl,
            postfixed_name: postfixedName,
            pluralized_breed_name: pluralizedBreedName,
            secondary_photo_url: secondaryPhotoUrl,
            standard_color_text: standardColorText,
            url
          } = breed;

          const photoUrl = isNonStandard
            ? nonStandardSecondaryPhotoUrl
            : secondaryPhotoUrl;
          const colorText = isNonStandard
            ? nonStandardColorText
            : standardColorText;
          const displayName =
            currentBreedId === breed.id || isNonStandard ? postfixedName : name;

          return (
            <div className="mb4 mb0-lg relative ph3 no-outline" key={id}>
              <SmartLink to={url}>
                <div className="mb4 br1 overflow-hidden">
                  <div className="transition grow f0 br1 overflow-hidden">
                    <SmartImage
                      Tag="div"
                      crop
                      lazy
                      className="zoom cover"
                      height={IMAGE_WIDTH}
                      src={photoUrl}
                      width={IMAGE_WIDTH}
                    />
                  </div>
                </div>
                <div className="flex">
                  <h5 className="mb1 mb0-lg f3 f4-md lh-normal flex-auto w0 ws-normal">
                    {displayName}
                  </h5>
                  {currentBreedId === id && (
                    <TooltipWrapper
                      className="lh-1 v-mid"
                      minWidth="300px"
                      title={
                        // eslint-disable-next-line react/no-danger
                        <p dangerouslySetInnerHTML={{ __html: colorText }} />
                      }
                    >
                      <Icon
                        name="help"
                        width={ICON_WIDTH}
                        height={ICON_WIDTH}
                      />
                    </TooltipWrapper>
                  )}
                </div>
                {!window.__DPL_BOT_UA && (
                  <p className="mb1 mb0-lg f2">{availabilityCountText}</p>
                )}
              </SmartLink>
              {breedOverviewPageUrl && (
                <a
                  className="mb1 mb0-lg f2 fw-medium midnight ws-normal"
                  href={breedOverviewPageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn about {pluralizedBreedName}
                </a>
              )}
            </div>
          );
        })}
      </HorizontalSlider>
    </div>
  );
}

BreederProfileRelatedBreedsList.propTypes = {
  currentBreedId: PropTypes.number.isRequired,
  relatedBreeds: PropTypes.arrayOf(
    PropTypes.shape({
      availability_count_text: PropTypes.string,
      breed_overview_page_url: PropTypes.string,
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      non_standard: PropTypes.bool,
      non_standard_color_text: PropTypes.string,
      non_standard_secondary_photo_url: PropTypes.string,
      postfixed_name: PropTypes.string.isRequired,
      pluralized_breed_name: PropTypes.string,
      secondary_photo_url: PropTypes.string,
      standard_color_text: PropTypes.string,
      url: PropTypes.string
    })
  ).isRequired
};
